import { TenantsService } from './../../../services/tenants.service';
import { getTenantName, Tenant } from 'src/app/models/tenant.model';
import { Component, OnInit, ViewChild } from '@angular/core';
import { SelectOptionsService } from '../../../services/select-options.service';
import { OwnersService } from '../../../services/owners.service';
import { getOwnerName, Owner } from '../../../models/owner.model';
import { PersonType } from '../../../models/person-type.model';
import { UntypedFormGroup, UntypedFormControl, Validators, UntypedFormArray, AbstractControl } from '@angular/forms';
import { ErrorResponse } from '../../../models/error.model';
import { UtilsService } from '../../../services/utils.service';
import { ActivatedRoute, Router } from '@angular/router';
import { MatStepper } from '@angular/material/stepper';
import { CSVColumnMap } from '../../generics/bulk-creator/bulk-creator.component';
import { generatePhysicalPersonFG } from 'src/app/models/physical-person.model';
import { generateLegalPersonFG } from 'src/app/models/legal-person.model';

@Component({
    selector: 'app-edit-tenant',
    templateUrl: './edit-tenant.component.html',
    styleUrls: ['./edit-tenant.component.scss']
})
export class EditTenantComponent implements OnInit {

    getTenantName = getTenantName;

    tenant: Tenant;

    tenantFG = new UntypedFormGroup({
        email: new UntypedFormControl('', [Validators.required]),
        tipoPersona: new UntypedFormControl('FISICA', [Validators.required]),
        personaFisica: generatePhysicalPersonFG('TENANT'),
        personaJuridica: generateLegalPersonFG()
    });

    get physicalPersonFG() {
        return this.tenantFG.controls.personaFisica as UntypedFormGroup;
    }

    get legalPersonFG() {
        return this.tenantFG.controls.personaJuridica as UntypedFormGroup;
    }

    get personFG() {
        return this.personType === 'FISICA' ? this.physicalPersonFG : this.legalPersonFG;
    }

    get representativeFG() {
        return this.personFG.controls.representante as UntypedFormGroup;
    }

    get addressFG() {
        return this.personFG.controls.direccion as UntypedFormGroup;
    }

    @ViewChild('stepper', { static: true })
    stepper: MatStepper;

    get personType() {
        return this.tenantFG.controls.tipoPersona.value as PersonType;
    }

    constructor(
        private route: ActivatedRoute,
        private tenantsService: TenantsService,
        public utilsService: UtilsService,
        private router: Router
    ) { }

    ngOnInit(): void {
        this.route.params.subscribe(params => {
            if (params?.id) {
                this.tenantsService.getTenantById(params.id).subscribe(tenant => {
                    this.tenant = tenant;
                    this.fillFormsWithTenantData();
                }, (error: ErrorResponse) => this.utilsService.alert('Error al obtener datos del inquilino', error.error?.descripcion));
            }
        });
    }

    fillFormsWithTenantData() {
        setTimeout(() => {
            this.utilsService.fillFormWithPartialData(this.tenantFG, this.tenant);
            const tieneRepresentante = this.tenant.tipoPersona === 'FISICA' ? !!this.tenant.personaFisica.representante : !!this.tenant.personaJuridica.representante;
            this.representativeFG.controls.tieneRepresentante.setValue(tieneRepresentante, { emitEvent: false });

            // Establecer el tipo de documento según qué campo tenga valor
            if (this.tenant.tipoPersona === 'FISICA') {
                if (this.tenant.personaFisica.dni) {
                    this.physicalPersonFG.controls.tipoDocumento?.setValue('DNI', { emitEvent: false });
                    this.physicalPersonFG.controls.dni?.setValue(this.tenant.personaFisica.dni, { emitEvent: false });
                } else if (this.tenant.personaFisica.pasaporte) {
                    this.physicalPersonFG.controls.tipoDocumento?.setValue('PASAPORTE', { emitEvent: false });
                    this.physicalPersonFG.controls.pasaporte?.setValue(this.tenant.personaFisica.pasaporte, { emitEvent: false });
                }
            }

            this.tenantFG.controls.tipoPersona.disable();
            if (this.personType === 'FISICA') {
                this.physicalPersonFG.controls.sexo.disable();
            } else {
                this.legalPersonFG.controls.cuit.disable();
            }

            this.tenantFG.markAllAsTouched();
        });
    }

    isValid(control: AbstractControl) {
        if (control instanceof UntypedFormGroup || control instanceof UntypedFormArray) {
            return Object.keys(control.controls).every(key => this.isValid(control.controls[key]));
        } else if (control instanceof UntypedFormControl) {
            return control.status === 'VALID' || control.status === 'DISABLED';
        } else {
            return false;
        }
    }

    isGeneralSectionValid() {
        let valid = true;
        if (!this.isValid(this.tenantFG.controls.email)) {
            valid = false;
        }
        if (this.personType === 'FISICA') {
            // Validamos persona física
            if (
                !this.isValid(this.physicalPersonFG.controls.dni) ||
                !this.isValid(this.physicalPersonFG.controls.sexo) ||
                !this.isValid(this.physicalPersonFG.controls.nombre) ||
                !this.isValid(this.physicalPersonFG.controls.apellido) ||
                !this.isValid(this.physicalPersonFG.controls.celular) ||
                !this.isValid(this.physicalPersonFG.controls.fechaNacimiento)
            ) {
                valid = false;
            }
        } else {
            // Validamos persona jurídica
            if (
                !this.isValid(this.legalPersonFG.controls.cuit) ||
                !this.isValid(this.legalPersonFG.controls.razonSocial) ||
                !this.isValid(this.legalPersonFG.controls.tipoSociedad) ||
                !this.isValid(this.legalPersonFG.controls.rubro) ||
                !this.isValid(this.legalPersonFG.controls.situacionIVA) ||
                !this.isValid(this.legalPersonFG.controls.celular)
            ) {
                valid = false;
            }
        }
        return valid;
    }

    isRepresentativeSectionValid() {
        if (this.representativeFG.controls.tieneRepresentante.value) {
            return this.isValid(this.representativeFG);
        } else {
            return true;
        }
    }

    isAddressSectionValid() {
        if (this.personType === 'JURIDICA') {
            return this.isValid(this.addressFG);
        } else {
            return true;
        }
    }

    getResultingTenant() {
        const value = this.tenantFG.getRawValue();
        const tenant: Tenant = {
            id: this.tenant?.id,
            email: value.email,
            tipoPersona: value.tipoPersona,
            personaFisica: value.tipoPersona === 'FISICA' ? {
                dni: value.personaFisica.tipoDocumento === 'DNI' ? value.personaFisica.dni : null,
                pasaporte: value.personaFisica.tipoDocumento === 'PASAPORTE' ? value.personaFisica.pasaporte : null,
                sexo: value.personaFisica.sexo,
                nombre: value.personaFisica.nombre,
                apellido: value.personaFisica.apellido,
                celular: value.personaFisica.celular,
                fechaNacimiento: value.personaFisica.fechaNacimiento,
                representante: value.personaFisica.representante.tieneRepresentante ? {
                    nombre: value.personaFisica.representante.nombre,
                    apellido: value.personaFisica.representante.apellido,
                    fechaNacimiento: value.personaFisica.representante.fechaNacimiento,
                    cargo: value.personaFisica.representante.cargo,
                    fechaPoder: value.personaFisica.representante.fechaPoder,
                    poderAdjunto: value.personaFisica.representante.poderAdjunto,
                    nombreCargo: value.personaFisica.representante.nombreCargo,
                    nroAsambleaEstatuto: value.personaFisica.representante.nroAsambleaEstatuto,
                    mail: value.personaFisica.representante.mail,
                    celular: value.personaFisica.representante.celular,
                    dni: value.personaFisica.representante.dni,
                    direccion: {
                        tipoCalle: value.personaFisica.representante.direccion.tipoCalle,
                        calle: value.personaFisica.representante.direccion.calle,
                        numero: value.personaFisica.representante.direccion.numero,
                        piso: value.personaFisica.representante.direccion.piso,
                        departamento: value.personaFisica.representante.direccion.departamento,
                        codigoPostal: value.personaFisica.representante.direccion.codigoPostal,
                        localidad: value.personaFisica.representante.direccion.localidad,
                    },
                } : null,
            } : null,
            personaJuridica: value.tipoPersona === 'JURIDICA' ? {
                cuit: value.personaJuridica.cuit,
                razonSocial: value.personaJuridica.razonSocial,
                tipoSociedad: value.personaJuridica.tipoSociedad,
                tipoSociedadPersonalizado: value.personaJuridica.tipoSociedadPersonalizado,
                rubro: value.personaJuridica.rubro,
                rubroPersonalizado: value.personaJuridica.rubroPersonalizado,
                situacionIVA: value.personaJuridica.situacionIVA,
                celular: value.personaJuridica.celular,
                direccion: {
                    tipoCalle: value.personaJuridica.direccion.tipoCalle,
                    calle: value.personaJuridica.direccion.calle,
                    numero: value.personaJuridica.direccion.numero,
                    piso: value.personaJuridica.direccion.piso,
                    departamento: value.personaJuridica.direccion.departamento,
                    codigoPostal: value.personaJuridica.direccion.codigoPostal,
                    localidad: value.personaJuridica.direccion.localidad,
                },
                representante: value.personaJuridica.representante.tieneRepresentante ? {
                    nombre: value.personaJuridica.representante.nombre,
                    apellido: value.personaJuridica.representante.apellido,
                    fechaNacimiento: value.personaJuridica.representante.fechaNacimiento,
                    cargo: value.personaJuridica.representante.cargo,
                    fechaPoder: value.personaJuridica.representante.fechaPoder,
                    poderAdjunto: value.personaJuridica.representante.poderAdjunto,
                    nombreCargo: value.personaJuridica.representante.nombreCargo,
                    nroAsambleaEstatuto: value.personaJuridica.representante.nroAsambleaEstatuto,
                    mail: value.personaJuridica.representante.mail,
                    celular: value.personaJuridica.representante.celular,
                    dni: value.personaJuridica.representante.dni,
                    direccion: {
                        tipoCalle: value.personaJuridica.representante.direccion.tipoCalle,
                        calle: value.personaJuridica.representante.direccion.calle,
                        numero: value.personaJuridica.representante.direccion.numero,
                        piso: value.personaJuridica.representante.direccion.piso,
                        departamento: value.personaJuridica.representante.direccion.departamento,
                        codigoPostal: value.personaJuridica.representante.direccion.codigoPostal,
                        localidad: value.personaJuridica.representante.direccion.localidad,
                    },
                } : null,
            } : null
        };
        return tenant;
    }

    createTenant() {
        this.tenantFG.markAllAsTouched();
        if (!this.isGeneralSectionValid()) {
            this.stepper.selectedIndex = 0;
            return;
        }
        if (!this.isRepresentativeSectionValid()) {
            this.stepper.selectedIndex = 1;
            return;
        }
        if (!this.isAddressSectionValid()) {
            this.stepper.selectedIndex = 2;
            return;
        }
        const tenant: Tenant = this.getResultingTenant();
        if (this.tenant) {
            this.tenantsService.editTenant(tenant).subscribe(_ => {
                this.router.navigate(['inquilinos']);
            }, (error: ErrorResponse) => this.utilsService.alert('Error al modificar inquilino', error.error?.descripcion));
        }
    }

}
